export interface RateEntry {
  label:     string
  value:     number
  change:    number   // bp change from previous meeting
  nextMeeting: string
  expectation: string
  asOf:      string
}

export interface MacroSeries {
  label:     string
  value:     number
  unit:      string
  trend:     'up' | 'down' | 'flat'
  yoy?:      number   // year-over-year %
  asOf:      string
  source:    string
}

export interface YieldPoint {
  maturity: string
  yield:    number
}

export interface MacroData {
  updatedAt:   string
  rates:       RateEntry[]
  inflation:   MacroSeries[]
  growth:      MacroSeries[]
  labor:       MacroSeries[]
  yieldCurve:  { us: YieldPoint[]; eu: YieldPoint[] }
  nextEvents:  Array<{ date: string; label: string; region: string; importance: 'high' | 'medium' }>
}

export const macro: MacroData = {
  updatedAt: '2026-10-31',
  // Quellen: Fed 28.10.2026 (Hold 3.75–4.00%, 10:2), EZB 29.10.2026 (Hold), BEA BIP Q3 29.10. (+3.4%), BEA PCE Sep, BLS NFP Sep, CME FedWatch 30.10., Yahoo Finance Schlusskurse 30.10.

  rates: [
    { label: 'Fed Funds', value: 3.875, change: 0, nextMeeting: '2026-12-09', expectation: 'Hold am 28.10. (10:2, Cook und Hammack wollten +25bp). Dezember-Hike ~74% (CME, 30.10.). Bis Sep 2027 ~4.7% eingepreist. Warsh: "Interest rates get in the cracks".', asOf: '2026-10-28' },
    { label: 'EZB',       value: 2.50,  change: 0, nextMeeting: '2026-12-17', expectation: 'Hold am 29.10., Hike diskutiert — Dezember möglich. Lagarde tritt im Februar 2027 vorzeitig zurück. Frankreich 10J > 5%.', asOf: '2026-10-29' },
    { label: 'SNB',       value: 1.00,  change: 0, nextMeeting: '2026-12-12', expectation: 'Hold erwartet. CHF als sicherer Hafen gefragt, importierte Öl-Inflation bleibt Risiko.', asOf: '2026-09-19' },
  ],

  inflation: [
    { label: 'CPI USA',       value: 3.7, unit: '%', trend: 'up', yoy: 3.7, asOf: '2026-10-14', source: 'BLS — Sep, Okt-Wert am 10.11.' },
    { label: 'Core CPI USA',  value: 2.6, unit: '%', trend: 'up', yoy: 2.6, asOf: '2026-10-14', source: 'BLS — Sep' },
    { label: 'PCE USA',       value: 3.6, unit: '%', trend: 'up', yoy: 3.6, asOf: '2026-10-29', source: 'BEA — Sep (Vormonat 3.4%)' },
    { label: 'PCE Kern',      value: 3.1, unit: '%', trend: 'up', yoy: 3.1, asOf: '2026-10-29', source: 'BEA — Sep, +0.3% MoM' },
    { label: 'CPI Euro',      value: 3.8, unit: '%', trend: 'up', yoy: 3.8, asOf: '2026-10-29', source: 'Eurostat / EZB — Schnellschätzung Okt am 4.11.' },
    { label: 'CPI CH',        value: 1.4, unit: '%', trend: 'up', yoy: 1.4, asOf: '2026-09-03', source: 'BFS' },
  ],

  growth: [
    { label: 'BIP USA Q3',    value: 3.4, unit: '%', trend: 'up',   asOf: '2026-10-29', source: 'BEA — annualisiert, Konsens 3.1–3.2%, Q2 2.2%' },
    { label: 'BIP Euro 2026', value: 0.9, unit: '%', trend: 'up',   asOf: '2026-09-10', source: 'EZB-Prognose' },
    { label: 'Konsumentenvertrauen', value: 91.7, unit: '', trend: 'up', asOf: '2026-10-27', source: 'Conference Board — Konsens 81.0, Vormonat 84.2' },
  ],

  labor: [
    { label: 'NFP USA Sep',          value: 29,      unit: 'k', trend: 'down', asOf: '2026-10-02', source: 'BLS — Konsens 89k; Okt-Wert am 6.11.' },
    { label: 'Erstanträge (4W-Ø)',   value: 196.75,  unit: 'k', trend: 'flat', asOf: '2026-10-24', source: 'DOL — Woche bis 24.10.' },
    { label: 'Arbeitslosigkeit USA', value: 4.1,     unit: '%', trend: 'flat', asOf: '2026-10-02', source: 'BLS — Okt-Prognose ~4.2%' },
    { label: 'Arbeitslosigkeit CH',  value: 2.4,     unit: '%', trend: 'flat', asOf: '2026-09-08', source: 'SECO' },
  ],

  // US 30.10.: 10Y 4.93% (Yahoo ^TNX), 13W 3.97% (^IRX), 2Y ~4.60% (ZeroHedge/Investing); übrige Laufzeiten geschätzt
  yieldCurve: {
    us: [
      { maturity: '3M',  yield: 3.97 },
      { maturity: '1Y',  yield: 4.35 },
      { maturity: '2Y',  yield: 4.60 },
      { maturity: '5Y',  yield: 4.72 },
      { maturity: '10Y', yield: 4.93 },
      { maturity: '30Y', yield: 5.30 },
    ],
    eu: [
      { maturity: '2Y',  yield: 2.58 },
      { maturity: '5Y',  yield: 2.85 },
      { maturity: '10Y', yield: 3.08 },
      { maturity: '30Y', yield: 3.28 },
    ],
  },

  nextEvents: [
    { date: '2026-11-01', label: 'OPEC+-Treffen (Nov-Quote 31.01 Mio. bpd gehalten)', region: 'Global', importance: 'medium' },
    { date: '2026-11-02', label: 'Apple Q4-Zahlen nach Börsenschluss (±4.5% eingepreist)', region: 'USA', importance: 'medium' },
    { date: '2026-11-03', label: 'US-Midterm-Wahlen; AMD-Zahlen', region: 'USA', importance: 'high' },
    { date: '2026-11-04', label: 'Euroraum HVPI Schnellschätzung (Prognose 3.9%)', region: 'EU', importance: 'medium' },
    { date: '2026-11-06', label: 'US-Arbeitsmarkt Oktober', region: 'USA', importance: 'high' },
    { date: '2026-11-10', label: 'US-CPI Oktober', region: 'USA', importance: 'high' },
    { date: '2026-11-17', label: 'Nvidia Q3 FY27', region: 'USA', importance: 'high' },
    { date: '2026-12-09', label: 'FOMC — Hike ~74% eingepreist', region: 'USA', importance: 'high' },
    { date: '2026-12-10', label: 'US-CPI November', region: 'USA', importance: 'high' },
  ],
}
